import React, {useMemo, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import {useTranslation} from 'react-i18next';
import {ISingleVoyage} from "@containers/singleVoyage/types/voyage";
import {ISyncItemVoyageTemplate, IVoyageTemplateSingleVoyage} from "@containers/singleVoyage/voyageTemplates/types";
import SyncMapVoyageTemplate, {
	IHighlightedStopSync
} from "@containers/singleVoyage/voyageTemplates/sync/map/SyncMapVoyageTemplate";
import RouteChangeLegend from "@containers/singleVoyage/voyageTemplates/routeChange/RouteChangeLegend";
import StopsLineSyncVoyageTemplate, {
	StopChangesListSyncVoyageTemplate
} from "@containers/singleVoyage/voyageTemplates/sync/detail/StopsLineSyncVoyageTemplate";
import {buildStopsDiff} from "@containers/singleVoyage/voyageTemplates/sync/helpers/buildStopsDiff";
import {
	formatDaySyncVoyageTemplate,
	formatLongDaySyncVoyageTemplate,
	formatRangeSyncVoyageTemplate,
	formatTimeSyncVoyageTemplate
} from "@containers/singleVoyage/voyageTemplates/sync/helpers/format";
import {sx} from './sx'

interface IProps {
	item: ISyncItemVoyageTemplate
	template: IVoyageTemplateSingleVoyage
	/** the voyage itself, for its route and stops; null while it loads */
	voyage: ISingleVoyage | null
}

const DetailSyncVoyageTemplate = ({item, template, voyage}: IProps) => {

	const {t} = useTranslation()

	const currentVoyage = voyage?.id === item.voyageId ? voyage : null

	const [highlighted, setHighlighted] = useState<IHighlightedStopSync | null>(null)

	const diff = useMemo(() => {
		return buildStopsDiff({template, voyage: currentVoyage, changes: item.changes})
	}, [template, currentVoyage, item])

	const oldRange = formatRangeSyncVoyageTemplate(item.plannedStart, item.plannedEnd)
	const newRange = formatRangeSyncVoyageTemplate(item.newPlannedStart, item.newPlannedEnd)
	const routeChanged = item.changes.some((change) => change.type === 'ROUTE')

	return (
		<Stack sx={sx.root}>
			<Stack sx={sx.header}>
				<Typography sx={sx.title}>
					{formatLongDaySyncVoyageTemplate(item.plannedStart)}
				</Typography>

				<Typography sx={sx.plate}>
					{item.licensePlate ?? '—'}
				</Typography>

				{oldRange === newRange
					? <Typography sx={sx.timeNew}>{newRange}</Typography>
					: <Typography sx={sx.timeNew}>
						<Box component={'span'} sx={sx.timeOld}>{oldRange}</Box>
						{' → '}
						{newRange}
					</Typography>}
			</Stack>

			{/* only the voyage as a whole is known to be hand-edited, not which of the differences came from that */}
			{item.outcome === 'DIVERGED' && <Stack sx={sx.diverged}>
                <EditNoteIcon sx={sx.divergedIcon}/>
                <Typography sx={sx.divergedText}>
					{t('singleVoyage.voyageTemplate.sync.divergedBanner', {
						date: item.updatedDateTime
							? `${formatDaySyncVoyageTemplate(item.updatedDateTime)} ${formatTimeSyncVoyageTemplate(item.updatedDateTime)}`
							: '—'
					})}
                </Typography>
            </Stack>}

			<Box sx={sx.map}>
				<SyncMapVoyageTemplate
					template={template}
					voyage={currentVoyage}
					changes={item.changes}
					highlighted={highlighted}
				/>
			</Box>

			<RouteChangeLegend
				items={['newRoute', 'oldRoute', 'changedRoute', 'added', 'changed', 'movedStop', 'oldPosition', 'removed']}
				variant={'row'}
			/>

			<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
				<Typography sx={sx.sectionLabel}>
					{t('singleVoyage.voyageTemplate.sync.stops')}
				</Typography>

				{routeChanged && <Typography sx={sx.plate}>
					{t('singleVoyage.voyageTemplate.sync.change.ROUTE')}
                </Typography>}
			</Stack>

			<StopsLineSyncVoyageTemplate diff={diff} onHighlight={setHighlighted}/>

			<StopChangesListSyncVoyageTemplate diff={diff}/>
		</Stack>
	);
};

export default DetailSyncVoyageTemplate;
